import { Fragment, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { window } from '../../globals';
import useTeamGroups from '../../hooks/api/teams/teamGroups/useTeamGroups';
import { useTeamsReleaseGroups } from '../../hooks/api/teams/useTeamReleaseGroups';
import useTeams from '../../hooks/api/teams/useTeams';
import useToastedMutation from '../../ReactQuery/useToastedMutation';
import { get } from '../../redux/api';
import TeamsModule from '../../redux/teams';
import utils from '../../utils';
import Card from '../generic/Card/Card';
import DropdownButton from '../generic/DropdownButton';
import LabeledCheckbox from '../generic/Forms/LabeledCheckbox';
import { icons, IconText } from '../generic/Icon';
import Select from '../generic/Select/Select';
import { queuedDownloadNotification } from '../JobNotificationHub/queuedDownloadNotification';
import { issueDashboardFeature } from '../settings/billing/features/consts';
import type { SelectOption, Task, TeamSelection } from '../../types';
import type { AddJobFn } from '../JobNotificationHub/types';
import type {
  QueuedReportTaskResponse,
  ReportDelivery,
} from '../../../shared/types/routes/queuedReports';

import DashboardReportCard from './DashboardReportCard';
import { GlobalIssueCSVExportMetricsPreview } from './GlobalIssueCSVExportMetricsPreview';

import css from './GlobalIssueCSVExportCard.module.less';

const GenerateReportIconText = <IconText icon={icons.addToProject} text="Generate report" />;

/**
 * Hand a queued Global Issue CSV Export to the notification queue. A download (the response carries a
 * `reportId`) gets a persistent toast linking to the stored zip; an email gets a plain completion toast.
 */
export function addQueuedExportToNotificationQueue(
  addJob: AddJobFn,
  task: Task | QueuedReportTaskResponse | undefined,
) {
  if (!task?.jobToken) {
    return;
  }
  if ('reportId' in task) {
    addJob(task.jobToken, queuedDownloadNotification('Global Issue CSV Export', task.reportId));
    return;
  }
  addJob(task.jobToken, {
    success: { content: 'Global Issue CSV Export report emailed.' },
    failure: { content: 'Global Issue CSV Export report failed.' },
  });
}

type Props = {
  onAddJobToNotificationQueue: AddJobFn;
};

/**
 * The "Global Issue CSV Export" card on the Reports dashboard. Scope comes from the "All projects" breadcrumb,
 * like the other report cards. When a team or team group is selected there (a team group is a `Teams` row, so
 * it arrives as its own id - the `teamId=[groupId]` rollup), the card reveals a release-group drill-down and two
 * include-flags: the flat issues list and/or the issue counts over time. Org-wide/unassigned falls back to the
 * plain issues export.
 */
export const GlobalIssueCSVExportCard: VoidFunctionComponent<Props> = ({ onAddJobToNotificationQueue }) => {
  const teamSelection = useSelector(TeamsModule.selectors.getTeamSelection);
  // A non-empty team/group selection is the drill-down context; `[]` is all projects, `'null'` is unassigned.
  const selectedTeamIds = Array.isArray(teamSelection) ? teamSelection : [];
  const isTeamContext = selectedTeamIds.length > 0;
  // The release-group picker spans the whole breadcrumb selection, so a stable key of the selected ids
  // drives both the candidate fetch and the drill-down reset.
  const selectionKey = selectedTeamIds.join(',');

  const [releaseGroupId, setReleaseGroupId] = useState<number | null>(null);
  const [shouldIncludeIssuesList, setShouldIncludeIssuesList] = useState(true);
  const [shouldIncludeDailyCounts, setShouldIncludeDailyCounts] = useState(false);

  // Reset the release-group drill-down when the breadcrumb selection changes, so a stale selection from the
  // previous scope isn't sent to the preview/export endpoints.
  useEffect(() => {
    setReleaseGroupId(null);
  }, [selectionKey]);

  // When a release group is selected the counts export is a release-comparison series (release_counts.csv)
  // instead of the team/org daily series (issue_counts.csv); both are driven by the same include flag.
  const isReleaseGroupScoped = releaseGroupId != null;

  const { releaseGroups } = useTeamsReleaseGroups(
    selectedTeamIds,
    { page: 1, pageSize: 100, sort: 'asc', skipPagination: true },
    isTeamContext,
  );
  const releaseGroupOptions: Array<SelectOption<number>> = releaseGroups.map((releaseGroup) => ({
    label: releaseGroup.title,
    value: releaseGroup.id,
  }));
  const selectedReleaseGroupOption = releaseGroupOptions.find((option) => option.value === releaseGroupId) ?? null;

  // Resolve the breadcrumb selection to names so we can show what's scoped (a team group is a `Teams` row too).
  const { data: teams } = useTeams(isTeamContext);
  const { data: teamGroups } = useTeamGroups(isTeamContext);
  const selectedNames = [...(teams ?? []), ...(teamGroups ?? [])]
    .filter((team) => selectedTeamIds.includes(team.id))
    .map((team) => team.name);

  // Pass the breadcrumb filter through: `'null'` (unassigned) as-is, a real selection scoped, all-projects off.
  let exportTeamIds: TeamSelection | undefined;
  if (teamSelection === 'null') {
    exportTeamIds = 'null';
  } else if (selectedTeamIds.length > 0) {
    exportTeamIds = selectedTeamIds;
  }
  const exportReleaseGroupId = isTeamContext && releaseGroupId != null ? releaseGroupId : undefined;

  const hasNothingSelected = isTeamContext && !shouldIncludeIssuesList && !shouldIncludeDailyCounts;

  // Both deliveries queue a background job that builds the bundle (issues list and/or counts), so a large org's
  // export isn't cut off by the request timeout. Email mails links to it; download stores it and the completion
  // toast links to it. The mutation surfaces its outcome as a toast and hands the job to the notification queue.
  const queueExport = useToastedMutation({
    mutationFn: async (delivery: ReportDelivery) => {
      const response = await get<Task | QueuedReportTaskResponse>('/api/v2/issues/csv/global', false, {
        ...(delivery === 'email' ? { email: true } : { delivery: 'async-download' }),
        ...(exportTeamIds ? { teamIds: exportTeamIds } : {}),
        ...(exportReleaseGroupId != null ? { releaseGroupId: exportReleaseGroupId } : {}),
        ...(isTeamContext
          ? { includeIssuesList: shouldIncludeIssuesList, includeDailyCounts: shouldIncludeDailyCounts }
          : {}),
      });
      if (!response.ok) {
        throw new Error('Failed to queue CSV export');
      }
      return response.data;
    },
    successToastMessage: 'Your report is being generated.',
    errorToastMessage: 'Failed to generate the report.',
    onSuccess: (task) => addQueuedExportToNotificationQueue(onAddJobToNotificationQueue, task),
  });

  // DropdownButton's onSelect passes the selected item's eventKey; coerce it to our delivery method.
  const generate = (eventKey?: unknown) => {
    // Premium gate: mirrors the legacy card's paywall (the export is also enforced server-side).
    if (!utils.verify_subscription('premium')) {
      window.showPlanSelectorModal({ focusFeature: issueDashboardFeature });
      return;
    }

    queueExport.mutate(eventKey === 'email' ? 'email' : 'download');
  };

  const chipLabels = selectedNames.length > 0 ? selectedNames : ['the selected team'];
  const description = isTeamContext ? (
    <>
      Issue reports in CSV format for{' '}
      {chipLabels.map((name, index) => (
        <Fragment key={name}>
          {index > 0 && ', '}
          <span className={css.chip}>{name}</span>
        </Fragment>
      ))}
      .
    </>
  ) : (
    'Organization-wide issue reports in CSV format. Contains separate reports for license, security, and quality issues.'
  );

  return (
    <DashboardReportCard title="Global Issue CSV Export" description={description}>
      {isTeamContext && (
        <div className={css.teamPanelWrapper}>
          <Card variant="outline" color="gray" padding="none" isFullWidth>
            <Card.Content>
              <div className={css.teamPanel}>
                <div className={css.field}>
                  <span className={css.fieldLabel}>Release group</span>
                  <Select<SelectOption<number>>
                    id="teamGroupReleaseGroup"
                    isClearable
                    placeholder="All release groups"
                    value={selectedReleaseGroupOption}
                    options={releaseGroupOptions}
                    onChange={(option) => setReleaseGroupId(option?.value ?? null)}
                  />
                </div>

                <div className={css.checkboxes}>
                  <LabeledCheckbox
                    label="Include issues list"
                    tooltip="A CSV of every current finding (license, security, and quality) with its dependency, project, and status."
                    isChecked={shouldIncludeIssuesList}
                    onChange={(_event, checked) => setShouldIncludeIssuesList(checked)}
                  />
                  <LabeledCheckbox
                    label="Include issue counts over time"
                    tooltip={isReleaseGroupScoped
                      ? 'A CSV comparing issue counts across each release in the group: new, remediated, and unchanged.'
                      : 'A CSV of daily active, ignored, and remediated issue counts per category over the last 30 days.'}
                    isChecked={shouldIncludeDailyCounts}
                    onChange={(_event, checked) => setShouldIncludeDailyCounts(checked)}
                  />
                </div>

                {/* Counts are release-scoped when a release group is selected, else across the selected teams. */}
                <GlobalIssueCSVExportMetricsPreview
                  teamIds={selectedTeamIds}
                  releaseGroupId={releaseGroupId}
                  enabled={isTeamContext}
                />
              </div>
            </Card.Content>
          </Card>
        </div>
      )}

      <DropdownButton
        id="GlobalIssueCSVExportOptions"
        title={GenerateReportIconText}
        size="large"
        onSelect={generate}
        disabled={hasNothingSelected || queueExport.isPending}
      >
        <DropdownButton.Item eventKey="download">Download</DropdownButton.Item>
        <DropdownButton.Item eventKey="email">Email</DropdownButton.Item>
      </DropdownButton>
    </DashboardReportCard>
  );
};
